/**
 * Red Yard Research S.R.L.
 *
 * EssilorLuxottica WorkOrder
 * Back-End Services
 * 
 * DEV NOTE: token console logging available in axios interceptor, search for axios.interceptors.request.use
 */

import axios from "axios";
import { devTenantServiceRequestConfiguration, luxTenantServiceRequestConfiguration } from "./ServiceRequestConfiguration";


import { PublicClientApplication, Configuration, AccountInfo, AuthenticationResult, InteractionRequiredAuthError } from "@azure/msal-browser";


interface FormData<T> {
    [index: string]: T
}

export const configuration = luxTenantServiceRequestConfiguration;

const previewChanghesHighlightColor = "#c7e7ff";



let interactionInProgress: boolean = false;
const TOKEN_STORAGE_KEY = "auth_token";
const MSAL_ACCOUNT_KEY = "msal.account.keys";

const MSAL_CONFIG: Configuration = {
    auth: {
        clientId: configuration.appClientId,
        authority: configuration.appAuthority,
    },
    cache: {
        cacheLocation: configuration.cacheLocation,
        storeAuthStateInCookie: false,
    },
};

const LOGIN_REQUEST = {
    scopes: configuration.loginRequestScopes,
};

const TOKEN_REQUEST = {
    scopes: configuration.tokenRequestScopes,
};

const GRAPH_CONFIG = {
    graphUsersEndpoint: configuration.graphUsersEndpoint,
};

let refreshing: Promise<string> | null = null;

export class PopupBlockedError extends Error {
    constructor(message = "Popup blocked") {
        super(message);
        this.name = "PopupBlockedError";
    }
}

export const isPopupBlockedError = (e: unknown) =>
    e instanceof PopupBlockedError;



const isMsalPopupError = (e: any): boolean => {
    const s = (e?.errorCode || e?.code || e?.message || "").toString().toLowerCase();
    return s.includes("popup") || s.includes("popups_not_supported") || s.includes("monitor_window_timeout");
};

const getFreshToken = async (): Promise<string> => {
    const account = PUBLIC_CLIENT_APPLICATION.getActiveAccount();
    if (!account) throw new Error("No active account");

    const request = { scopes: LOGIN_REQUEST.scopes, account };

    try {
        const res = await PUBLIC_CLIENT_APPLICATION.acquireTokenSilent(request);
        return res.accessToken;
    } catch (err) {
        console.warn("Silent token failed, retrying with forceRefresh...", err);

        if (refreshing) return refreshing;

        refreshing = PUBLIC_CLIENT_APPLICATION
            .acquireTokenSilent({ ...request, forceRefresh: true })
            .then(r => r.accessToken)
            .catch(async () => {
                const res = await PUBLIC_CLIENT_APPLICATION.acquireTokenPopup(request);
                return res.accessToken;
            })
            .finally(() => (refreshing = null));

        return refreshing;
    }
};

export const PUBLIC_CLIENT_APPLICATION = new PublicClientApplication(MSAL_CONFIG);



async function initializeMsal(): Promise<void> {
    await PUBLIC_CLIENT_APPLICATION.initialize();

    try {
        const redirectResponse = await PUBLIC_CLIENT_APPLICATION.handleRedirectPromise();
        if (redirectResponse && redirectResponse.account) {
            PUBLIC_CLIENT_APPLICATION.setActiveAccount(redirectResponse.account);

            localStorage.removeItem('redirect_uri');
            //window.location.reload();

        }
    } catch (error) {
        console.error("Error handling redirect response:", error);
    }
}

// initializeMsal();


const acquireFreshToken = async (): Promise<string | null> => {
    const account = PUBLIC_CLIENT_APPLICATION.getActiveAccount();
    if (!account) {
        console.warn('No active account. Cannot acquire token.');
        return null;
    }

    try {
        const result = await PUBLIC_CLIENT_APPLICATION.acquireTokenSilent({
            scopes: configuration.loginRequestScopes,
            account,
        });

        return result.accessToken;
    } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
            console.warn('Token expired or refresh failed → redirecting to login...');
            await PUBLIC_CLIENT_APPLICATION.loginRedirect({
                scopes: configuration.loginRequestScopes,
                redirectUri: configuration.redirectUri,
            });

            window.location.reload();

        } else {
            console.error('Failed to acquire token:', error);
        }

        return null;
    }
};

/**
 * Sets the interaction progress flag to indicate ongoing or completed user interaction.
 *
 * @param flag - A boolean value to set the interaction progress state.
 */
const setInteractionInProgress = (flag: boolean): void => {
    interactionInProgress = flag;
};

/**
 * Retrieves the keys of MSAL accounts stored in local or session storage.
 *
 * @returns The first MSAL account key as a string, or null if no account keys exist.
 */
const getMsalAccountKeys = (): string | null => {
    let msalAccountKeys =
        localStorage.getItem("msal.account.keys") || sessionStorage.getItem("msal.account.keys");

    if (msalAccountKeys) {
        msalAccountKeys = JSON.parse(msalAccountKeys)[0];
        return msalAccountKeys;
    }

    return null;
};

/**
 * Checks if a user is authenticated by verifying the existence of an MSAL account key.
 *
 * @returns A boolean indicating whether the user is authenticated.
 */
const isAuthenticated = (): boolean => {
    // return !!getMsalAccountKeys();
    return PUBLIC_CLIENT_APPLICATION.getAllAccounts().length > 0;
};

/**
 * Handles user sign-in using MSAL.
 * 
 * - Initiates a login popup for user authentication.
 * - Sets the active account in MSAL upon successful login.
 * - Retrieves an access token silently for subsequent API calls.
 * - Optionally stores a callback URL for redirection after login.
 * 
 * @param {string} [callbackUrl] - Optional URL to redirect the user after login.
 * @returns {Promise<void>} Resolves when the sign-in process completes.
 */

/*
const handleSignIn = async (callbackUrl?: string): Promise<void> => {
    if (!interactionInProgress) {
        setInteractionInProgress(true);

        try {
            if (callbackUrl != null) {
                localStorage.setItem('redirect_uri', callbackUrl);
            }

            await PUBLIC_CLIENT_APPLICATION.loginRedirect({
                ...LOGIN_REQUEST,
                redirectUri: configuration.redirectUri,
            });

            window.location.reload();


        } catch (error) {
            console.error("Error during sign in:", error);
            setInteractionInProgress(false);
        }
    }
};

*/

const handleSignIn = async (callbackUrl?: string): Promise<void> => {
    if (interactionInProgress) return;
    setInteractionInProgress(true);

    try {
        if (callbackUrl) localStorage.setItem("redirect_uri", callbackUrl);

        const result = await PUBLIC_CLIENT_APPLICATION.loginPopup({
            ...LOGIN_REQUEST,
            redirectUri: configuration.redirectUri,
        });

        if (result?.account) {
            PUBLIC_CLIENT_APPLICATION.setActiveAccount(result.account);
        }

        const redirect = localStorage.getItem("redirect_uri");
        if (redirect) {
            localStorage.removeItem("redirect_uri");
            window.location.assign(redirect);
        }
    } catch (error: any) {
        if (isMsalPopupError(error)) {
            setInteractionInProgress(false);
            throw new PopupBlockedError();
        }

        // await PUBLIC_CLIENT_APPLICATION.loginRedirect({ ...LOGIN_REQUEST, redirectUri: configuration.redirectUri });

        console.error("Error during sign in:", error);
        setInteractionInProgress(false);
    }
};

/**
 * Stores the provided token in the specified cache location.
 * 
 * - Saves the token in either localStorage or sessionStorage, based on the configuration.
 * - Logs an error if an invalid cache location is specified.
 * 
 * @param {string} token - The access token to be stored.
 */


/**
 * Retrieves the stored token from the cache.
 * 
 * - Checks both localStorage and sessionStorage for the token.
 * - Returns the token if found, otherwise returns null.
 * 
 * @returns {string | null} The retrieved token or null if not found.
 */




/**
 * Removes the stored token from the cache.
 * 
 * - Deletes the token from both localStorage and sessionStorage.
 */
const removeToken = (): void => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(MSAL_ACCOUNT_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(MSAL_ACCOUNT_KEY);
};

/**
 * Retrieves the authenticated user's account information.
 * 
 * @returns {AccountInfo | null} The active account if the user is authenticated, otherwise null.
 */
const getIdentity = (): AccountInfo | null => {
    if (isAuthenticated()) {
        return PUBLIC_CLIENT_APPLICATION.getActiveAccount();
    }
    return null;
};

/**
 * Handles user sign-out by clearing the authentication token and redirecting to the logout endpoint.
 * 
 * @async
 * @returns {Promise<void>} Resolves when the sign-out process completes.
 */
const handleSignOut = async (): Promise<void> => {
    if (interactionInProgress || !isAuthenticated()) {
        return;
    }

    setInteractionInProgress(true);
    try {
        const account = PUBLIC_CLIENT_APPLICATION.getActiveAccount();
        if (!account) {
            console.error("No active account found.");
            return;
        }

        await PUBLIC_CLIENT_APPLICATION.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
            onRedirectNavigate: () => false,
            account: account,
        });


    } catch (error) {
        console.error("Error during sign out:", error);
    } finally {
        setInteractionInProgress(false);
    }
};

/**
 * Refreshes the authentication token silently and updates the stored token.
 * 
 * @async
 * @returns {Promise<void>} Resolves when the token is successfully refreshed.
 * Logs an error if the refresh process fails.
 */
const handleRefreshToken = async (): Promise<void> => {
    try {
        const tokenResponse: AuthenticationResult = await PUBLIC_CLIENT_APPLICATION.acquireTokenSilent(
            TOKEN_REQUEST
        );

    } catch (error) {
        console.error("Error refreshing token:", error);
    }
};

/**
 * Acquires an authentication token for the active account.
 * 
 * - Tries to obtain the token silently using `acquireTokenSilent`.
 * - Falls back to a popup method (`acquireTokenPopup`) if silent acquisition fails.
 * - Updates the stored token upon successful acquisition.
 * 
 * @async
 * @returns {Promise<string | null>} The acquired token if successful, or `null` if the acquisition fails.
 */
const acquireToken = async (): Promise<string | null> => {
    try {
        const account = PUBLIC_CLIENT_APPLICATION.getActiveAccount();
        if (!account) {
            console.error("No active account found. User might not be logged in.");
            return null;
        }

        const response = await PUBLIC_CLIENT_APPLICATION.acquireTokenSilent({
            scopes: LOGIN_REQUEST.scopes,
            account,
        });


        return response.accessToken;
    } catch (error) {
        console.error("Error acquiring token silently:", error);

        try {
            const response = await PUBLIC_CLIENT_APPLICATION.acquireTokenPopup({
                scopes: TOKEN_REQUEST.scopes,
            });

            return response.accessToken;
        } catch (popupError) {
            console.error("Error acquiring token via popup:", popupError);
            return null;
        }
    }
};

/**
 * Generates the Authorization header with the Bearer token.
 * 
 * - Acquires the token using `acquireToken`.
 * - Formats the token into a Bearer Authorization header.
 * 
 * @async
 * @returns {Promise<string | null>} The Authorization header as a string if the token is available, or `null` otherwise.
 */
const authHeader = async (): Promise<string | null> => {
    const token = await acquireToken();
    return token ? `Bearer ${token}` : null;
};

/**
 * Axios interceptor for attaching the Authorization header to all requests.
 * 
 * - Fetches the latest token using the `authHeader` function.
 * - Adds the `Authorization` header to the request configuration if a valid token is available.
 * - Ensures requests are properly authenticated.
 * 
 * @param {AxiosRequestConfig} config - The outgoing request configuration.
 * @returns {AxiosRequestConfig} - The modified request configuration with the Authorization header.
 * @throws {Promise} - Rejects the request if an error occurs during configuration.
 */
axios.interceptors.request.use(
    async (config) => {
        if (config.skipInterceptor) {
            return config;
        }

        const token = await authHeader();
        if (token) {

            config.headers.Authorization = token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axios.interceptors.request.use(async (config) => {
    if ((config as any).skipInterceptor) return config;

    const token = await getFreshToken();
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
    return config;
});

axios.interceptors.response.use(
    (res) => res,
    async (error) => {
        const { response, config } = error || {};
        if (response?.status === 401 && !config._retry) {
            config._retry = true;
            const fresh = await getFreshToken();
            config.headers.Authorization = `Bearer ${fresh}`;
            return axios.request(config);
        }
        return Promise.reject(error);
    }
);

const reportAccess = (account: any): void => {
    try {
        axios.post(
            `${configuration.baseUrl}${configuration.restServicesPath}logger/access`,
            account,
            {
                headers: {
                    "Content-Type": "application/json",
                }
            }
        );

    } catch (error) {
        handleError(error);
        console.error("Unexpected Error:", error);
    }
}

const reportError = (error: any): void => {
    try {
        axios.post(
            `${configuration.baseUrl}${configuration.restServicesPath}logger/ui`,
            error,
            {
                headers: {
                    "Content-Type": "application/json",
                }
            }
        );

    } catch (error) {
        console.error("Unexpected Error:", error);
    }
}

/**
 * Handles errors during API calls or other operations.
 * 
 * - Differentiates between Axios errors and unexpected errors.
 * - Logs the error status and message for Axios errors.
 * - Logs a generic message for unexpected errors.
 * 
 * @param {any} error - The error object caught during an operation.
 */
const handleError = (error: any): void => {
    if (axios.isAxiosError(error)) {
        console.error(`API Error: ${error.response?.status} - ${error.response?.statusText}`);

    } else {

    }
};


const handleForcedPrefilledFields = async (
    document_id: string,
    project_id?: string,
    template_name?: string
): Promise<Record<string, any> | false> => {
    try {

        const doc = await getDocument(document_id, project_id as any, template_name as any);

        const tplPrefilled =
            doc?.template?.prefilled ??
            doc?.data?.template?.prefilled ??
            doc?.data?.prefilled ??
            {};


        return tplPrefilled || {};
    } catch (error) {

        console.error('[forced-prefill] error while reading template.prefilled:', error);
        return false;
    }
};



/**
 * Fetches pre-filled fields for a specific project.
 * 
 * - Sends a GET request to retrieve pre-filled fields associated with the given project ID.
 * 
 * @param {string} project_id - The ID of the project to retrieve pre-filled fields for.
 * @returns {Promise<any>} - The pre-filled fields data or `false` if an error occurs.
 */
const hadlePreFilledFields = async (project_id: string): Promise<any> => {
    if (isAuthenticated()) {
        const path = `request/pre_filled/${project_id}`;
        if (project_id) {
            try {
                const response = await axios.get(
                    `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                    {
                        headers: {
                            "Content-Type": "application/json",

                        },
                        withCredentials: true,
                    }
                );
                return response.data.data;
            } catch (error) {
                handleError(error);
                return false;
            }
        }
    }
    return false;
}

const spInflight = new Map<string, Promise<any>>();
const spKey = (project_id: string, list_name: string, odata_query: string) =>
    `${project_id}::${list_name}::${odata_query ?? ''}`;


const pickFieldValue = (item: any, sp_field: string) => {
    if (!item || typeof item !== 'object' || !sp_field) return undefined;


    if (sp_field in item) return item[sp_field];


    const fmt = `${sp_field}@OData.Community.Display.V1.FormattedValue`;
    if (fmt in item) return item[fmt];


    const decoded = sp_field.replace(/_x0020_/g, ' ');
    if (decoded in item) return item[decoded];
    const decodedFmt = `${decoded}@OData.Community.Display.V1.FormattedValue`;
    if (decodedFmt in item) return item[decodedFmt];


    const keyCi = Object.keys(item).find((k) => k.toLowerCase() === sp_field.toLowerCase());
    if (keyCi) return item[keyCi];


    const nested = (item as any).Fields || (item as any).fields || (item as any).Value || (item as any).value || (item as any).d;
    if (nested && typeof nested === 'object') {
        if (sp_field in nested) return nested[sp_field];
        if (fmt in nested) return nested[fmt];
        if (decoded in nested) return nested[decoded];
        if (decodedFmt in nested) return nested[decodedFmt];
        const keyCiN = Object.keys(nested).find((k) => k.toLowerCase() === sp_field.toLowerCase());
        if (keyCiN) return nested[keyCiN];
    }

    return undefined;
};

const hadleSpMetadata = async (
    sp_field: string,
    template_field: string,
    project_id: string,
    list_name: string,
    odata_query: string = ''
): Promise<any> => {
    if (!isAuthenticated()) return false;
    if (!project_id || !list_name) return false;

    const path = `request/spmetadata`;
    const url = `${configuration.baseUrl}${configuration.restServicesPath}${path}`;
    const payload = { project_id, list_name, odata_query };
    const key = spKey(project_id, list_name, odata_query);


    if (spInflight.has(key)) {
        try {
            const data = await spInflight.get(key)!;
            const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
            return pullField(arr, sp_field) ?? false;
        } catch {
            return false;
        }
    }

    const req = axios
        .post(url, payload, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
            timeout: 20000,
        })
        .then((response) => response.data?.data ?? response.data)
        .catch((error) => {
            handleError(error);
            return false;
        })
        .finally(() => {
            spInflight.delete(key);
        });

    spInflight.set(key, req);

    try {
        const data = await req;
        const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (!Array.isArray(arr) || arr.length === 0) return false;
        return pullField(arr, sp_field) ?? false;
    } catch {
        return false;
    }
};


const humanizeSpValue = (v: any) => {
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'string') {
        const low = v.trim().toLowerCase();
        if (low === 'true') return 'Yes';
        if (low === 'false') return 'No';
        return v;
    }
    if (v && typeof v === 'object') {
        if ('Title' in v) return (v as any).Title;
        if ('Label' in v) return (v as any).Label;
        if ('LookupValue' in v) return (v as any).LookupValue;
        if ('Value' in v) return (v as any).Value;
        if (Array.isArray(v) && v.length > 0) {
            const first = v[0];
            if (first && typeof first === 'object') {
                if ('Title' in first) return first.Title;
                if ('Label' in first) return first.Label;
                if ('LookupValue' in first) return first.LookupValue;
                if ('Value' in first) return first.Value;
            }
            return String(v[0] ?? '');
        }
    }
    return v;
};


const pullField = (arr: any[], sp_field: string) => {
    for (const item of arr) {
        const raw = pickFieldValue(item, sp_field);
        if (raw !== undefined && raw !== null && !(typeof raw === 'string' && raw.trim() === ''))
            return humanizeSpValue(raw);
    }
    return undefined;
};

export async function validatePrefillValue(
    templateName: string,
    fieldName: string,
    raw: any
): Promise<any | undefined> {
    try {
        const res = await validateField(templateName, fieldName, raw);
        if (res?.valid) return res.normalizedValue ?? raw; 
    } catch { }
    return undefined;
}

export async function validatePrefillsMap(
    templateName: string,
    fields: Array<{ name: string; fetch?: string | string[] }>,
    prefills: Record<string, any>
): Promise<Record<string, any>> {
    const out: Record<string, any> = {};
    const tasks: Promise<void>[] = [];

    for (const f of fields) {
        if (!f.fetch || Array.isArray(f.fetch)) continue;
        const fetchKey = String(f.fetch);
        if (fetchKey.startsWith("sp:")) continue;

        const v = prefills[fetchKey];
        if (v === undefined) continue;

        tasks.push(
            validatePrefillValue(templateName, f.name, v).then((ok) => {
                if (ok !== undefined) out[f.name] = ok;
            })
        );
    }

    await Promise.allSettled(tasks);
    return out;
}


export async function hadleSpMetadataValidated(
  templateName: string,
  sp_field: string,
  template_field: string,
  project_id: string,
  list_name: string,
  odata_query: string = ""
): Promise<any | false> {
  const raw = await hadleSpMetadata(sp_field, template_field, project_id, list_name, odata_query);
  if (raw === false || raw === undefined || raw === null || (typeof raw === "string" && raw.trim() === "")) {
    return false; 
  }
  const ok = await validatePrefillValue(templateName, template_field, raw);
  return ok === undefined ? false : ok;
}


export async function loadValidatedPrefilled(
  templateName: string,
  fields: Array<{ name: string; fetch?: string | string[] }>,
  project_id: string
): Promise<Record<string, any>> {
  const raw = await hadlePreFilledFields(project_id);
  if (!raw || typeof raw !== "object") return {};
  return validatePrefillsMap(templateName, fields, raw);
}

/**
 * Fetches user roles for a specific project and user.
 * 
 * - Sends a GET request to retrieve roles associated with the given project ID and username.
 * 
 * @param {string} project_id - The ID of the project.
 * @param {string} user_name - The name of the user to retrieve roles for.
 * @returns {Promise<any>} - The user roles data or `false` if an error occurs.
 */
const handleRoles = async (project_id: string, user_name: string): Promise<any> => {
    if (isAuthenticated()) {
        const path = `request/roles/${project_id}/${user_name}`;
        if (project_id) {
            try {
                const response = await axios.get(
                    `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                        withCredentials: true,
                    }
                );
                return response.data.data;
            } catch (error) {
                handleError(error);
                return false;
            }
        }
    }
    return false;
}


/**
 * Sends a GET request to the specified API path
 * 
 * @param {string} path - The API endpoint to fetch data from
 * @returns {Promise<any>} - The response data or `false` if an error occurs
 */
const getRequest = async (path: string): Promise<any> => {
    if (isAuthenticated()) {
        try {
            const response = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                {
                    headers: {
                        "Content-Type": "application/json",

                    },
                    withCredentials: true,
                }
            );
            return response.data.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};

/**
 * Sends a POST request to the specified API path with the provided data
 * 
 * @param {string} path - The API endpoint to send data to
 * @param {any} data - The payload to include in the request
 * @returns {Promise<any>} - The response data or `false` if an error occurs
 */
const postRequest = async (path: string, data: any): Promise<any> => {
    if (isAuthenticated()) {
        try {
            const response = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                data,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};

const getDocumentsSpot = async (fields: string[]): Promise<any> => {
    const payload = { fields };

    return await postRequest("/documents/spot", payload);
};




/**
 * Retrieves field data for a specific template and step number
 * 
 * @param {any} template_name - The name of the template
 * @param {any} step_number - The step number to retrieve fields for
 * @returns {Promise<any>} - The field data or `false` if an error occurs
 */
const getFields = async (template_name: any, step_number: any): Promise<any> => {
    const path = `/templates/${template_name}/step/${step_number}`;
    if (isAuthenticated()) {
        try {
            const response = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};

const getStepFields = async (documentId: string, stepNumber: any): Promise<any> => {
    const path = `documents/${documentId}`;
    if (isAuthenticated()) {
        try {
            const response = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data.data.template.steps[parseInt(stepNumber) - 1].fields;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};



/**
 * Retrieves step information for a specific template
 * 
 * @param {string} template_name - The name of the template
 * @returns {Promise<any>} - Step data or `false` if an error occurs
 */
const getSteps = async (template_name: string): Promise<any> => {
    if (isAuthenticated()) {
        const path = `templates/${template_name}`;
        try {
            const response = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data.data.steps;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};

const getDocumentSteps = async (documentId: string): Promise<any> => {
    if (isAuthenticated()) {
        const path = `documents/${documentId}`;
        try {
            const response = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data.data.template.steps;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
}

const checkSwitchableTemplate = async (documentId: string): Promise<any> => {
    if (isAuthenticated()) {
        const path = `documents/switchable`;
        const requestBody = { documentId }
        try {
            const response = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
}

const confirmTemplateSwitch = async (documentId: string): Promise<any> => {
    if (isAuthenticated()) {
        const path = `documents/confirmSwitch`;
        const requestBody = { documentId }
        try {
            const response = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
}

/**
 * Retrieves a document by its ID, or creates a new one if it doesn't exist
 * 
 * @param {any} documentId - The ID of the document to retrieve
 * @param {any} projectId - Optional project ID for creating a document
 * @param {any} templateName - Optional template name for creating a document
 * @returns {Promise<any>} - The document data or `false` if an error occurs
 */


const getDocument = async (
    documentId: string | undefined,
    projectId?: string | null,
    templateName?: string | null

): Promise<any> => {
    const qs = new URLSearchParams();
    if (projectId) qs.set("projectId", projectId);
    if (templateName) qs.set("templateName", templateName);


    const url = `${configuration.baseUrl}${configuration.restServicesPath}documents/${documentId}${qs.toString() ? `?${qs.toString()}` : ""}`;

    try {
        const response = await axios.get(url, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
        });


        if (response.data?.data?.creatable === true) {

            const data = response.data?.data;

            return {
                success: true,
                action: "CONFIRM_CREATE",
                payload: {
                    documentId,
                    projectId,
                    templateName,
                    creatable: true,
                    projectSub: data?.projectSub ?? undefined,
                    templateAlias: data?.templateAlias ?? undefined,
                    projectName: data?.projectName ?? undefined,
                    brand: data?.brand ?? undefined,
                }
            };
        }

        if (response.data?.success) return response.data;
        throw new Error("Unexpected response shape");
    } catch (error: any) {

        return { success: false, action: "ERROR" };
    }

};


/**
 * Creates a new document with the specified details
 * 
 * @param {string} documentName - The name of the document to create
 * @param {string} description - A description of the document
 * @param {string} templateName - The template name for the document
 * @param {string} projectId - The ID of the associated project
 * @param {any} documentId - Optional ID for the document
 * @returns {Promise<any>} - The created document data or `false` if an error occurs
 */
const createDocument = async (documentName: string, description: string, templateName: string, projectId: string, documentId?: any): Promise<any> => {
    const path = "/documents/create";
    const data: any = {
        name: documentName,
        description,
        templateName,
        projectId,
    };

    if (documentId) {
        data._id = documentId;
    }

    if (isAuthenticated()) {
        try {
            const response = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                data,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};

/**
 * Updates an existing document with new data
 * 
 * @param {string} documentId - The ID of the document to update
 * @param {any} data - The new data for the document
 * @returns {Promise<any>} - The updated document data or `false` if an error occurs
 */
const patchDocument = async (documentId: string, data: any): Promise<any> => {
    const path = `/documents/${documentId}`;

    if (isAuthenticated()) {
        try {
            const response = await axios.patch(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                data,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};


/**
 * Updates the values of a document
 * 
 * @param {string} documentId - The ID of the document to update
 * @param {any} data - The new values to set in the document
 * @returns {Promise<any>} - The updated document data or `false` if an error occurs
 */
const updateDocumentValues = async (documentId: string, data: any): Promise<any> => {
    const path = `/documents/${documentId}`;

    const values = {
        values: data
    };

    if (isAuthenticated()) {
        try {
            const response = await axios.patch(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                values,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};


/**
 * Updates the step history of a document
 * 
 * @param {string} documentId - The ID of the document
 * @param {number} stepNumber - The step number to update
 * @returns {Promise<any>} - The updated step history or `false` if an error occurs
 */
const updateStepHistory = async (documentId: string, stepNumber: number): Promise<any> => {
    const path = `/documents/${documentId}/${stepNumber}`;

    if (isAuthenticated()) {
        try {
            const identity = getIdentity();
            if (!identity) {
                throw new Error("No active identity found");
            }

            const userData = {
                userAlias: identity.name,
                userName: identity.username,
            };

            const response = await axios.patch(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                userData,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};

/**
 * Retrieves the values of a specific document
 * 
 * @param {string} documentId - The ID of the document
 * @returns {Promise<any>} - The document values or `false` if an error occurs
 */
const getDocumentValues = async (documentId: string): Promise<any> => {
    const path = `documents/${documentId}`;

    if (isAuthenticated()) {
        try {
            const response = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data.data.values;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};

/**
 * Deletes a specific document
 * 
 * @param {string} documentId - The ID of the document to delete
 * @returns {Promise<any>} - The response data or `false` if an error occurs
 */
const deleteDocument = async (documentId: string): Promise<any> => {
    const path = `/documents/${documentId}`;

    if (isAuthenticated()) {
        try {
            const response = await axios.delete(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (error) {
            handleError(error);
            return false;
        }
    }
    return false;
};


/**
 * Generates a unique PDF filename based on the current date and time
 * 
 * @param {string} templateName - The template name to include in the filename
 * @returns {string} - A timestamped filename in the format: <templateName>_YYYYMMDD_HHMMSS.pdf
 */
const generateFileName = (templateName: string): string => {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${templateName}_${year}${month}${day}_${hours}${minutes}${seconds}.pdf`;
};

/**
 * Generates default properties for a new document, including a timestamped name and creation description
 * 
 * @returns {object} - An object containing the document's name and description
 */
const generateDocumentProperties = (): any => {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return {
        name: `New Document ${year}${month}${day}_${hours}${minutes}${seconds}`,
        description: `Created at ${year}-${month}-${day} ${hours}:${minutes}`,
    };
};

/**
 * Converts a file into a Data URI format for easy transmission and embedding
 * 
 * @param {File} file - The file to be converted
 * @returns {Promise<string | ArrayBuffer | null>} - A promise that resolves to the Data URI or rejects with an error
 */
const getDataUri = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });
};


/**
 * Updates the form data and modified form data based on the input event
 * 
 * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e - The event triggered by the input change
 * @param {any} formData - The current form data state
 * @param {React.Dispatch<React.SetStateAction<any>>} setFormData - The state setter for form data
 * @param {any} modifiedFormData - The current modified form data state
 * @param {React.Dispatch<React.SetStateAction<any>>} setModifiedFormData - The state setter for modified form data
 */
const updatePayload = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formData: any,
    setFormData: React.Dispatch<React.SetStateAction<any>>,
    modifiedFormData: any,
    setModifiedFormData: React.Dispatch<React.SetStateAction<any>>
): Promise<void> => {
    const { name, value, type, files, multiple } = e.target as HTMLInputElement;

    if (type === "file" && files) {
        if (!multiple) {
            const file = files[0];
            const dataUri = await getDataUri(file);
            setFormData({
                ...formData,
                [name]: dataUri,
            });
            setModifiedFormData({
                ...modifiedFormData,
                [name]: dataUri,
            });
        } else {
            const dataUriArray = await Promise.all(Array.from(files).map(getDataUri));
            setFormData({
                ...formData,
                [name]: dataUriArray,
            });
            setModifiedFormData({
                ...modifiedFormData,
                [name]: dataUriArray,
            });
        }
    } else if (type === "checkbox") {
        const checkboxes = document.querySelectorAll(
            `input[name="${name}"]`
        ) as NodeListOf<HTMLInputElement>;

        const selectedValues = Array.from(checkboxes)
            .filter((checkbox) => checkbox.checked)
            .map((checkbox) => checkbox.value);

        setFormData({
            ...formData,
            [name]: selectedValues,
        });
        setModifiedFormData({
            ...modifiedFormData,
            [name]: selectedValues,
        });
    } else {
        setFormData({
            ...formData,
            [name]: value,
        });
        setModifiedFormData({
            ...modifiedFormData,
            [name]: value,
        });
    }
};

/**
 * Organizes a flat payload into a nested object structure.
 * Converts keys with dot notation into nested objects and arrays.
 * Handles recursive organization for nested object keys.
 *
 * @param payload - A flat object where keys may include dot notation indicating nesting.
 * @returns A nested object structure derived from the input payload.
 */
const organizePayloadAsObjects = (payload: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};

    Object.keys(payload).forEach(key => {
        const parts = key.split('.');

        if (parts.length > 1) {
            const baseKey = parts[0];
            const index = parts[1];

            if (!result[baseKey]) {
                result[baseKey] = [];
            }

            const obj: Record<string, any> = {};
            obj[index] = payload[key];
            result[baseKey].push(obj);
        } else if (typeof payload[key] === "object" && payload[key] !== null) {
            result[key] = organizePayloadAsObjects(payload[key]);
        } else {
            result[key] = payload[key];
        }
    });

    return result;
};



const documentValidator = async (document_id: any): Promise<any> => {
    if (isAuthenticated()) {
        try {
            const documentValues = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}/documents/${document_id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            const documentData = documentValues.data.data.values;

            const validationResults = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}/documents/validate/${document_id}`,
                documentData,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            return validationResults
        } catch (err: any) {
            console.error(err)
            return null;
        }
    }
}



const pdfFinalChecker = async (document_id: any): Promise<any> => {
    if (isAuthenticated()) {
        try {
            const documentValues = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}/documents/${document_id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            const documentData = documentValues.data.data.values;
            const templateName = documentValues.data.data.templateName;




            const response = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}templates/${templateName}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,

                }
            );

            const steps = response.data.data.steps

            let fields: any = [];
            let missingFields: any = [];
            let mandatoryMissingFields: any = [];
            const results: any = {};

            results.check = true;

            steps.forEach((step: any, index: number) => {
                if (step.fields.length) {
                    step.fields.forEach((field: any) => {
                        field.stepName = step.stepName
                        field.stepNumber = index + 1
                        fields.push(field)
                    })
                }
            })

            fields.forEach((field: any) => {
                if (!documentData[field.name]) {
                    if (!field.nullable) {
                        missingFields.push({
                            field: field.label,
                            stepName: field.stepName,
                            stepNumber: field.stepNumber
                        })
                    } else {
                        mandatoryMissingFields.push({
                            field: field.label,
                            stepName: field.stepName,
                            stepNumber: field.stepNumber
                        })
                    }
                }


            })



            if (missingFields.length) {
                results.check = false;
            }

            results.missingFields = missingFields;
            results.mandatoryMissingFields = mandatoryMissingFields;

            return results;

        } catch (err: any) {
            if (
                err.response &&
                err.response.headers["content-type"].includes("application/json")
            ) {
                const errorData = await err.response.data.text();
                if (configuration.debug) {
                    console.error("Error:", JSON.parse(errorData));
                }
            } else {
                if (configuration.debug) {
                    console.error(err);
                }
            }

            return null;
        }
    }
}

/**
 * Generates a PDF document based on the given document ID and prompts the user to download it.
 *
 * @param document_id - The unique identifier of the document to generate the PDF for.
 * @returns A Promise resolving to void.
 */
const generatePDF = async (document_id: any): Promise<void> => {
    if (isAuthenticated()) {
        try {
            const documentValues = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}/documents/${document_id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            const documentData = documentValues.data.data.values;
            const templateName = documentValues.data.data.templateName;

            const path = `documents/generate/${templateName}/${document_id}`;

            const response = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                documentData,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                    responseType: "blob",
                }
            );

            const fileName = response.headers["content-disposition"]
                .split("filename=")[1]
                .replace(/['"]/g, "");

            const fileUrl = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );

            const link = document.createElement("a");
            link.href = fileUrl;
            link.setAttribute("download", fileName || generateFileName(templateName));
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err: any) {
            if (
                err.response &&
                err.response.headers["content-type"].includes("application/json")
            ) {
                const errorData = await err.response.data.text();
                if (configuration.debug) {
                    console.error("Error:", JSON.parse(errorData));
                }
            } else {
                if (configuration.debug) {
                    console.error("Error during PDF file generation", err);
                }
            }
        }
    }
};

/**
 * Generates a preview of a PDF document with optional highlighted changes.
 *
 * @param document_id - The unique identifier of the document to preview.
 * @param previewData - Optional data to be highlighted in the preview.
 * @returns A Promise resolving to a string containing the file URL of the generated preview.
 */
const generatePreview = async (
    document_id: any,
    previewData?: any,
    highlight?: boolean
): Promise<any> => {
    if (highlight == undefined) {
        highlight = false
    }
    if (isAuthenticated()) {
        try {
            const documentValues = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}documents/${document_id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );



            let documentData: any;

            if (!previewData) {
                documentData = documentValues.data.data.values || {};


                if (documentData) {
                    for (const [key, value] of Object.entries(documentData)) {
                        if (!value) {
                            delete (documentData as Record<string, any>)[key];
                        }
                    }
                }

            } else {

                let prefilledData = documentValues.data.data.values

                if (prefilledData) {
                    for (const [key, value] of Object.entries(prefilledData)) {
                        if (!value) {
                            delete (prefilledData as Record<string, any>)[key];
                        }
                    }
                }

                documentData = {
                    ...prefilledData,
                    ...previewData,
                };
            }




            const path = `documents/generate/preview/${document_id}?highlight=${highlight}`;

            const response = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                documentData,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                    responseType: "blob",
                }
            );

            const fileName = response.headers["content-disposition"]
                .split("filename=")[1]
                .replace(/['"]/g, "");

            const fileUrl = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );

            return fileUrl;
        } catch (err: any) {
            if (
                err.response &&
                err.response.headers["content-type"].includes("application/json")
            ) {
                const errorData = await err.response.data.text();
                if (configuration.debug) {
                    console.error("Error:", JSON.parse(errorData));
                }
            } else {
                if (configuration.debug) {
                    console.error("Error during PDF file generation", err);
                }
            }
        }
    }
};

const generatePDFForce = async (document_id: string) => {
    if (isAuthenticated()) {
        try {
            const documentValues = await axios.get(
                `${configuration.baseUrl}${configuration.restServicesPath}/documents/${document_id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );
            const documentData = documentValues.data.data.values;
            const templateName = documentValues.data.data.templateName;

            const path = `documents/generate/force/${document_id}`;

            const response = await axios.post(
                `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
                documentData,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                    responseType: "blob",
                }
            );

            const fileName = response.headers["content-disposition"]
                .split("filename=")[1]
                .replace(/['"]/g, "");

            const fileUrl = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );

            const link = document.createElement("a");
            link.href = fileUrl;
            link.setAttribute("download", fileName || generateFileName(templateName));
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err: any) {
            if (
                err.response &&
                err.response.headers["content-type"].includes("application/json")
            ) {
                const errorData = await err.response.data.text();
                if (configuration.debug) {
                    console.error("Error:", JSON.parse(errorData));
                }
            } else {
                if (configuration.debug) {
                    console.error("Error during PDF file generation", err);
                }
            }
        }
    }
};

const validateField = async (
    templateName: string,
    fieldName: string,
    rawValue: string,
    documentId?: string,
    context?: any
): Promise<any> => {
    if (!isAuthenticated()) return null;

    try {
        const path = `validation/field`;
        const payload: any = {
            templateName,
            fieldName,
            rawValue,
        };
        if (documentId) payload.documentId = documentId;
        if (context) payload.context = context;

        const response = await axios.post(
            `${configuration.baseUrl}${configuration.restServicesPath}${path}`,
            payload,
            {
                headers: { "Content-Type": "application/json" },
                withCredentials: true,
            }
        );

        return response.data;
    } catch (err: any) {
        if (
            err?.response &&
            err.response.headers?.["content-type"]?.includes("application/json")
        ) {
            try {
                const errorData = await err.response.data.text?.();
                if (configuration.debug) {
                    console.error("Field validation error:", JSON.parse(errorData));
                }
            } catch {
                if (configuration.debug) console.error("Field validation error:", err);
            }
        } else {
            if (configuration.debug) console.error("Network/unknown error:", err);
        }
        return { valid: false, errors: [{ code: "NETWORK_ERROR", message: "Unable to validate field" }] };
    }
};



export {
    initializeMsal,
    handleSignIn,
    isAuthenticated,
    getRequest,
    getIdentity,
    acquireFreshToken,

    handleSignOut,
    postRequest,
    createDocument,
    patchDocument,
    deleteDocument,
    documentValidator,
    generatePDF,
    generatePDFForce,
    getDataUri,
    updatePayload,
    handleRefreshToken,
    removeToken,
    getFields,
    getStepFields,
    updateDocumentValues,
    getDocumentValues,
    updateStepHistory,
    generatePreview,
    hadlePreFilledFields,
    hadleSpMetadata,
    getDocumentsSpot,
    handleForcedPrefilledFields,
    handleRoles,
    getSteps,
    getDocumentSteps,
    getDocument,
    checkSwitchableTemplate,
    confirmTemplateSwitch,
    reportError,
    pdfFinalChecker,
    validateField,
    previewChanghesHighlightColor
};